import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Observable, of } from 'rxjs';

import { ProjectType } from '@mirage-projects/cw-models';

import { MagnetService } from '../../abstract-classes/magnet.service';
import { UI_CONFIGURATION_TOKEN } from '../../tokens';
import { Skin } from '../skin/skin.enum';
import { SkinService } from '../skin/skin.service';
import { TranslationService } from '../translate/translation.service';
import { helpUrl } from './help-url.enum';
import {
    UiConfiguration,
    UiConfigurationApp,
    UiConfigurationLanguageGroup,
    UiConfigurationLanguageGroupByEnv,
    UiConfigurationLegal,
    UiConfigurationViewer,
    ViewerType,
} from './ui-configuration';
/**
 * The UI configuration for all the applications
 */
@Injectable({ providedIn: 'root' })
export class UiConfigurationService {
    readonly FORBIDDEN = 'forbidden';
    readonly ON = 'forceOn';
    readonly COLLAGE_PROJECT_VIEWER = 'projectViewer';
    readonly SCAN_PROJECT_VIEWER = 'scanProjectViewer';
    readonly SITE_SCAN_PROJECT_VIEWER = 'cr';
    readonly JOB_SITE_PROJECT_VIEWER = 'jobSiteViewer';
    readonly MAP_UI_CONFIGURATION = {
        '/viewer': this.COLLAGE_PROJECT_VIEWER,
        '/scan-viewer': this.SCAN_PROJECT_VIEWER,
        '/site-viewer': this.JOB_SITE_PROJECT_VIEWER,
    };
    private uiConfigurationApp: UiConfigurationApp = null as any;

    constructor(
        private magnetService: MagnetService,
        private translationService: TranslationService,
        private skinService: SkinService,
        @Inject(DOCUMENT) private document: Document,
        @Inject(UI_CONFIGURATION_TOKEN) private uiConfigurationJson: UiConfiguration,
        private router: Router
    ) {}

    init(): Observable<void> {
        this.uiConfigurationApp = this.parseConfiguration(this.magnetService.getAppName(), this.uiConfigurationJson);
        this.skinService.setSkin(this.uiConfiguration.skin ?? Skin.default);
        this.setDocumentTitle();
        return of();
    }

    /**
     * Sets the current browser tab title
     */
    setDocumentTitle(): void {
        this.document.title = this.title;
    }

    /**
     *  Returns the help url based on the current environment and help option provided
     */
    getHelpUrl(content: helpUrl = helpUrl.DEFAULT): string {
        if (!this.uiConfigurationApp) {
            return '';
        }

        const env = this.magnetService.getEnvName() as keyof UiConfigurationLanguageGroupByEnv;

        const currentLanguage = this.translationService.currentLang.substring(0, 2) as keyof UiConfigurationLanguageGroup;

        const url = this.uiConfigurationApp.help?.[env]?.[currentLanguage] ?? null;

        return url ? `${url}${content}` : '';
    }

    /**
     *  Returns the legal object
     */
    getLegal(): UiConfigurationLegal | undefined {
        return this.uiConfigurationApp?.legal;
    }

    /**
     * Returns the current viewer
     */
    getUiViewerConfiguration(viewerName?: ViewerType): UiConfigurationViewer | undefined {
        const view = viewerName ?? this.getCurrentView();

        if (!this.uiConfigurationApp?.viewers) {
            return;
        }

        return this.uiConfigurationApp.viewers[view];
    }

    /**
     * Gets the current view based on the path
     */
    getCurrentView(): ViewerType {
        const url = this.router.url;
        const indexEnd = url.indexOf('/', 1);
        const path = url.substring(0, indexEnd);
        const view = (this.MAP_UI_CONFIGURATION as any)[path];

        return view;
    }

    /**
     * Returns the current vierver's workflow
     */
    getWorkflowName(viewerName: ViewerType): string | undefined {
        const config: UiConfigurationViewer | undefined = this.getUiViewerConfiguration(viewerName);

        return config?.workflowName;
    }

    getCreateProjectForbiddenFields(createProjectConfig: any, type: ProjectType | ''): string[] {
        switch (type) {
            case ProjectType.SCAN:
                return createProjectConfig?.scan?.forbiddenFields;
            case ProjectType.COLLAGE_TYPE:
                return createProjectConfig?.generic?.forbiddenFields;
            default:
                return [];
        }
    }

    isMobile(): boolean {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    }

    private parseConfiguration(key: any, data: any): UiConfigurationApp {
        const configData: any = data;
        let result = {};

        if (configData[key]?.extends) {
            const extended: string = configData[key].extends;

            if (!configData[extended]) {
                throw new Error('UiConfiguration - no extended node found');
            }

            const parsed: UiConfigurationApp = this.parseConfiguration(extended, data);
            result = this.isObjectNotEmpty(parsed) ? parsed : configData[extended];
        }

        this.mergeConfiguration(result, configData[key]);

        return result;
    }

    private isObjectNotEmpty(parsed: any): boolean {
        return Object.keys(parsed).length !== 0;
    }

    private mergeConfiguration(target: any, source: any): void {
        for (const key in source) {
            if (typeof source[key] !== 'object') {
                target[key] = source[key];
                continue;
            }

            if (target[key]) {
                this.mergeConfiguration(target[key], source[key]);
            } else {
                target[key] = source[key];
            }
        }
    }

    /**
     * App's configuration
     */
    get uiConfiguration(): UiConfigurationApp {
        return this.uiConfigurationApp;
    }

    /**
     * Browser panel title
     */
    get title(): string {
        return this.uiConfigurationApp.title ? this.uiConfigurationApp.title : 'Collage Web';
    }
}
